/**
 * Shared contract for the Activity Log viewers.
 *
 * Both portals (`/admin/activity_logs` and `/events/:slug/activity_logs`) render the
 * same components, so the prop shapes live here. Everything arrives from the Rails
 * controllers as snake_case JSON — the keys below mirror `ActivityLogQuery::FILTER_KEYS`
 * and the `*_json` serializers, so do not rename them without changing the controllers.
 *
 * Timestamps arrive as ISO8601 strings (`Time#iso8601`).
 */
import dayjs from 'dayjs'

/** Outcomes recorded by `Activity::Recorder` (mirrors `ActivityLog::OUTCOMES`). */
export const ACTIVITY_LOG_OUTCOMES = ['success', 'failure', 'denied'] as const

export type ActivityLogOutcome = (typeof ACTIVITY_LOG_OUTCOMES)[number]

/**
 * Marker `Activity::ChangeSet` writes when a field changed but its values are not
 * safe to store (denylisted, unknown, or non-scalar). Only the field name survives.
 */
export const REDACTED_CHANGE_MARKER = '[changed]'

/** Longest span, in days, the CSV export accepts (`ActivityLogQuery#date_range_valid_for_export?`). */
export const EXPORT_MAX_RANGE_DAYS = 90

/** Page sizes offered by the viewers; matches the controllers' `resolved_per_page` clamp. */
export const ACTIVITY_LOG_PAGE_SIZES = ['10', '25', '50', '100']

/**
 * One entry of the sanitized diff. A `[from, to]` pair carries the real values;
 * a bare string (always `"[changed]"`) means the values were redacted.
 */
export type ActivityLogChangeValue = [unknown, unknown] | string

export type ActivityLogRecordedChanges = Record<string, ActivityLogChangeValue>

/**
 * A single row of the trail.
 *
 * The `*_label` fields are conveniences computed by the model; they are optional so a
 * leaner serializer still typechecks — the components fall back to humanizing the raw
 * value. `source` has no label of its own: the model would only `humanize` it, which is
 * exactly what `humanizeToken` already does client-side.
 */
export interface ActivityLog {
  id: number
  occurred_at: string
  category: string
  category_label?: string | null
  action: string
  action_label?: string | null
  source: string
  outcome: ActivityLogOutcome
  actor_type: string | null
  actor_id: number | null
  actor_name: string | null
  actor_email: string | null
  actor_label?: string | null
  target_type: string | null
  target_id: number | null
  target_label: string | null
  event_id: number | null
  event_name?: string | null
  job_id: string | null
  recorded_changes: ActivityLogRecordedChanges
  metadata: Record<string, unknown>
}

/**
 * Filter state echoed back by the controllers.
 *
 * `log_action` — not `action` — because Rails reserves `params[:action]`.
 * `event_id` is admin-only; the event portal injects its scope structurally.
 */
export interface ActivityLogFilters {
  from_date?: string | null
  to_date?: string | null
  category?: string | null
  log_action?: string | null
  actor_type?: string | null
  actor_id?: string | number | null
  target_type?: string | null
  target_id?: string | number | null
  source?: string | null
  outcome?: string | null
  event_id?: string | number | null
  search?: string | null
}

/** Ordered filter keys, kept in sync with `ActivityLogQuery::FILTER_KEYS`. */
export const ACTIVITY_LOG_FILTER_KEYS = [
  'from_date',
  'to_date',
  'category',
  'log_action',
  'actor_type',
  'actor_id',
  'target_type',
  'target_id',
  'source',
  'outcome',
  'event_id',
  'search',
] as const satisfies readonly (keyof ActivityLogFilters)[]

export interface ActivityLogPagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

/**
 * An event the surface may offer in its dropdown. Ordered by name, never truncated —
 * the list is independent of which events happen to have rows, so an operator can
 * select an event to discover that it has no activity yet.
 */
export interface ActivityLogEventOption {
  id: number
  name: string
}

/**
 * Select options supplied by `ActivityLogQuery#filter_options`.
 *
 * The bar carries one dropdown, and only on the admin surface, where it lists every
 * event. `Events::ActivityLogsController` passes no `event_options` at all, so the
 * event portal receives an empty array and renders no select.
 */
export interface ActivityLogFilterOptions {
  events: ActivityLogEventOption[]
}

/** Antd `Tag` colors per outcome. Unknown outcomes fall back to the neutral tag. */
export const OUTCOME_TAG_COLORS: Record<ActivityLogOutcome, string> = {
  success: 'success',
  failure: 'error',
  denied: 'warning',
}

export function outcomeTagColor(outcome: string | null | undefined): string {
  if (!outcome) return 'default'
  return OUTCOME_TAG_COLORS[outcome as ActivityLogOutcome] || 'default'
}

/** `registrant.approve` -> `Registrant approve`, `actor_name` -> `Actor name`. */
export function humanizeToken(value: string | null | undefined): string {
  if (!value) return '—'

  const spaced = value.replace(/[._]/g, ' ').trim()
  if (!spaced) return '—'

  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export function formatOccurredAt(value: string | null | undefined): string {
  if (!value) return '—'

  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('MMM D, YYYY h:mm:ss A') : value
}

/** Renders any sanitized scalar (or leftover structure) as display text. */
export function formatChangeValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string') return value.length > 0 ? value : '(empty)'
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)

  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

export function isRedactedChange(value: ActivityLogChangeValue): boolean {
  return !Array.isArray(value)
}

export interface ActivityLogChangeRow {
  key: string
  field: string
  label: string
  from: string
  to: string
  redacted: boolean
}

/** Flattens `recorded_changes` into the field/from/to rows the detail panel renders. */
export function toChangeRows(
  changes: ActivityLogRecordedChanges | null | undefined,
): ActivityLogChangeRow[] {
  if (!changes) return []

  return Object.entries(changes).map(([field, value]) => {
    if (Array.isArray(value)) {
      return {
        key: field,
        field,
        label: humanizeToken(field),
        from: formatChangeValue(value[0]),
        to: formatChangeValue(value[1]),
        redacted: false,
      }
    }

    const marker = typeof value === 'string' && value.length > 0 ? value : REDACTED_CHANGE_MARKER

    return {
      key: field,
      field,
      label: humanizeToken(field),
      from: marker,
      to: marker,
      redacted: true,
    }
  })
}

/**
 * Builds the query string shared by pagination, filtering and the CSV export.
 * Blank values are dropped so the URL only carries filters that are actually set.
 */
export function activityLogQueryParams(
  filters: ActivityLogFilters,
  extra: Record<string, string | number | null | undefined> = {},
): Record<string, string> {
  const params: Record<string, string> = {}

  ACTIVITY_LOG_FILTER_KEYS.forEach((key) => {
    const value = filters[key]
    if (value === undefined || value === null || value === '') return

    params[key] = String(value)
  })

  Object.entries(extra).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return

    params[key] = String(value)
  })

  return params
}

export interface ActivityLogRangeValidity {
  valid: boolean
  /** Why the export is blocked, or what it will download when it is allowed. */
  reason: string
}

/**
 * Mirrors `ActivityLogQuery#date_range_valid_for_export?`: both dates present,
 * `from <= to`, and at most `EXPORT_MAX_RANGE_DAYS` days apart.
 */
export function validateExportRange(
  fromDate: string | null | undefined,
  toDate: string | null | undefined,
): ActivityLogRangeValidity {
  if (!fromDate || !toDate) {
    return {
      valid: false,
      reason: `Pick a from and to date (max ${EXPORT_MAX_RANGE_DAYS} days) to enable the export.`,
    }
  }

  const from = dayjs(fromDate)
  const to = dayjs(toDate)

  if (!from.isValid() || !to.isValid()) {
    return { valid: false, reason: 'The selected date range is not valid.' }
  }

  if (to.isBefore(from, 'day')) {
    return { valid: false, reason: 'The end date must be on or after the start date.' }
  }

  if (to.diff(from, 'day') > EXPORT_MAX_RANGE_DAYS) {
    return {
      valid: false,
      reason: `The date range must span ${EXPORT_MAX_RANGE_DAYS} days or less.`,
    }
  }

  return { valid: true, reason: 'Download the filtered activity as CSV.' }
}
